/**
 * Roles slice
 */

import { createAction, createSlice } from '@reduxjs/toolkit';

import { Permission } from '../Permissions/permissionsSlice';

export interface Role {
  _id?: string;
  id?: string;
  name: string;
  displayName: string;
  description?: string;
  permissions?: string[] | Permission[];
  isActive: boolean;
  isSystemRole?: boolean;
  createdAt?: number;
  updatedAt?: number;
}

const initialState = {
  roles: [] as Role[],
  currentRole: null as Role | null,
  loading: false,
  error: null as string | null,
};

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    setRoles(state, action) {
      state.roles = action.payload;
      state.loading = false;
      state.error = null;
    },
    setCurrentRole(state, action) {
      state.currentRole = action.payload;
      state.loading = false;
      state.error = null;
    },
    addRole(state, action) {
      state.roles.push(action.payload);
      state.loading = false;
      state.error = null;
    },
    updateRole(state, action) {
      const index = state.roles.findIndex(
        (r) => (r._id || r.id) === (action.payload._id || action.payload.id)
      );
      if (index !== -1) {
        state.roles[index] = action.payload;
      }
      if (state.currentRole && (state.currentRole._id || state.currentRole.id) === (action.payload._id || action.payload.id)) {
        state.currentRole = action.payload;
      }
      state.loading = false;
      state.error = null;
    },
    removeRole(state, action) {
      state.roles = state.roles.filter(
        (r) => (r._id || r.id) !== action.payload
      );
      state.loading = false;
      state.error = null;
    },
    setLoading(state, action) {
      state.loading = action.payload;
    },
    setError(state, action) {
      state.error = action.payload;
      state.loading = false;
    },
    clearError(state) {
      state.error = null;
    },
  },
});

// Reducer
export const rolesReducer = rolesSlice.reducer;

// Actions
export const {
  setRoles,
  setCurrentRole,
  addRole,
  updateRole,
  removeRole,
  setLoading,
  setError,
  clearError,
} = rolesSlice.actions;

// Async Actions
export const fetchRoles = createAction('ROLES/FETCH_ALL');
export const fetchRole = createAction<string>('ROLES/FETCH_ONE');
export const createRole = createAction<Omit<Role, '_id' | 'id'>>('ROLES/CREATE');
export const updateRoleAction = createAction<{ id: string; data: Partial<Role> }>('ROLES/UPDATE');
export const deleteRole = createAction<string>('ROLES/DELETE');
export const assignRoleToUser = createAction<{ userId: string; roleId: string }>('ROLES/ASSIGN');
export const removeRoleFromUser = createAction<string>('ROLES/REMOVE_FROM_USER');
export const getUsersByRole = createAction<string>('ROLES/GET_USERS_BY_ROLE');

